/**
 * DataLoader Component
 * Standardizes loading, error, and empty states across the application
 */

import React from 'react';
import { UseQueryResult } from '@tanstack/react-query';
import { Loader2, AlertCircle, Inbox } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface DataLoaderProps<TData = unknown, TError = unknown> {
  query: UseQueryResult<TData, TError>;
  children: (data: TData) => React.ReactNode;
  loadingComponent?: React.ReactNode;
  errorComponent?: React.ReactNode;
  emptyComponent?: React.ReactNode;
  showEmptyState?: boolean | ((data: TData) => boolean);
  className?: string;
  loadingClassName?: string;
  errorClassName?: string;
  emptyClassName?: string;
}

// Alternative props for direct usage without query object
interface DirectDataLoaderProps<TData = unknown> {
  data: TData | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch?: () => void;
  children: (data: NonNullable<TData>) => React.ReactNode;
  loadingComponent?: React.ReactNode;
  errorComponent?: React.ReactNode;
  emptyComponent?: React.ReactNode;
  emptyStateMessage?: string;
  showEmptyState?: boolean | ((data: TData) => boolean);
  className?: string;
  loadingClassName?: string;
  errorClassName?: string;
  emptyClassName?: string;
}

function resolveQueryState<TData, TError>(
  props: DataLoaderProps<TData, TError> | DirectDataLoaderProps<TData>
) {
  if ('query' in props) return props.query;
  return {
    data: props.data,
    error: props.error,
    isLoading: props.isLoading,
    isError: !!props.error,
    refetch: props.refetch || (() => {}),
  };
}

function isEmptyResult<TData>(
  showEmptyState: boolean | ((data: TData) => boolean),
  data: TData
): boolean {
  if (typeof showEmptyState === 'function') return showEmptyState(data);
  return (
    showEmptyState && (!data || (Array.isArray(data) && data.length === 0))
  );
}

export function DataLoader<TData = unknown, TError = unknown>(
  props: DataLoaderProps<TData, TError> | DirectDataLoaderProps<TData>
) {
  // Check if props is using query pattern or direct pattern
  const isQueryProps = 'query' in props;

  const {
    children,
    loadingComponent,
    errorComponent,
    emptyComponent,
    showEmptyState = false,
    className,
    loadingClassName,
    errorClassName,
    emptyClassName,
  } = props;

  const { data, error, isLoading, isError, refetch } = resolveQueryState(props);

  const emptyStateMessage =
    !isQueryProps && 'emptyStateMessage' in props
      ? props.emptyStateMessage
      : 'No data available';

  // Loading state
  if (isLoading) {
    if (loadingComponent) {
      return <>{loadingComponent}</>;
    }

    return (
      <div
        className={cn('flex items-center justify-center p-8', loadingClassName)}
        data-testid="loading"
      >
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Error state
  if (isError) {
    if (errorComponent) {
      return <>{errorComponent}</>;
    }

    const errorMessage =
      error instanceof Error ? error.message : 'An error occurred';

    return (
      <div className={cn('p-8', errorClassName)}>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription className="mt-2">
            <p>{errorMessage}</p>
            <Button
              onClick={() => refetch()}
              variant="outline"
              size="sm"
              className="mt-2"
            >
              Try again
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Empty state check
  if (isEmptyResult(showEmptyState, data as TData)) {
    if (emptyComponent) {
      return <>{emptyComponent}</>;
    }

    return (
      <div
        className={cn(
          'flex flex-col items-center justify-center p-8 text-center',
          emptyClassName
        )}
      >
        <Inbox className="h-12 w-12 text-muted-foreground mb-4" />
        <p className="text-muted-foreground">{emptyStateMessage}</p>
      </div>
    );
  }

  // Success state with data
  return (
    <div className={className}>{children(data as NonNullable<TData>)}</div>
  );
}

// Page skeleton for page-level loading states
export function PageSkeleton({
  className,
  showCards = false,
}: {
  className?: string;
  showCards?: boolean;
}) {
  return (
    <div className={cn('min-h-full max-w-7xl mx-auto p-6', className)}>
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-muted rounded w-64" />
        <div className="h-4 bg-muted rounded w-96" />
        {showCards && <div className="h-32 bg-muted rounded" />}
        <div className="h-64 bg-muted rounded" />
      </div>
    </div>
  );
}
